import type { FlightExplorerRequest } from '../explorer/search_source.js';
import type { FlightAirportSwitchView } from '../results/offer/offer_copy.js';
import { useDetailLayer, type RenderFlightDetail } from '../results/offer/detail_layer.js';
import type { FaresText } from './fares_copy.js';
import { FlightFaresPage, type FlightFaresPageProps } from './fares_page.js';
import type { FaresPageFlight, OpenFlightDetail } from './fares_page_shared.js';

/** Os textos do cabeçalho escrito do pedido na tela de tarifas. */
export type FaresRequestCopy = {
  /** `Rio de Janeiro → São Paulo · Ida e volta`. Duas chaves FECHADAS, sem
   *  fragmento colado: o idioma seguinte pode ter outra pontuação. */
  readonly subtitle: (route: string, trip: string) => string;
  readonly subtitleWithTravelers: (route: string, trip: string, travelers: string) => string;
  readonly trip: Readonly<Record<FlightExplorerRequest['mode'], string>>;
  readonly cabin: Readonly<Record<NonNullable<FlightExplorerRequest['cabin']>, string>>;
  readonly travelers: (count: number) => string;
};

/** O que a tela de tarifas por pedido lê do host: os textos da tela e do
 *  cabeçalho do pedido, o classificador, o idioma e o cadastro. */
export type FaresRequestText = FaresText & { readonly request: FaresRequestCopy };

/** A volta parte de outro aeroporto que o da chegada da ida (#4203)? A regra é
 *  do contrato do host, que também a usa fora da tela; `null` sem desvio. */
export type FlightAirportSwitchFn = (input: {
  outboundArrival: string;
  returnDeparture: string | undefined;
  requested: string | undefined;
}) => FlightAirportSwitchView | null;

export interface FlightRequestFaresPageProps extends Omit<
  FlightFaresPageProps,
  'subtitle' | 'cabin' | 'airportSwitch' | 'onOpenDetail' | 'text'
> {
  /** O pedido corrente — rota, forma e (quando o host sabe) quantas pessoas
   *  viajam. É o cabeçalho da página e o que decide o desvio de aeroporto. */
  readonly request: FlightExplorerRequest;
  readonly text: FaresRequestText;
  readonly airportSwitchOf: FlightAirportSwitchFn;
  readonly renderDetail: RenderFlightDetail;
}

/** `Rio de Janeiro → São Paulo · Ida e volta · 2 viajantes`. A contagem só entra
 *  quando o host a conhece (ver `FlightExplorerRequest.travelerCount`): a busca
 *  de voo não tem eixo de passageiros, então a casca do chat cai na frase curta.
 *  Cidade fora do cadastro repete o IATA — menor mal que um título com buraco. */
function subtitleOf(request: FlightExplorerRequest, text: FaresRequestText): string {
  const cityOf = (iata: string) => text.places.cityOf(iata) ?? iata;
  const first = request.legs[0];
  const route = first ? `${cityOf(first.origin)} → ${cityOf(first.destination)}` : '';
  const trip = text.request.trip[request.mode];
  const count = request.travelerCount;
  if (!count) return text.request.subtitle(route, trip);
  return text.request.subtitleWithTravelers(route, trip, text.request.travelers(count));
}

/** O drawer de detalhe/assentos da tela, com a mesma mecânica da lista: duas
 *  portas, um drawer só. */
function useFaresDetail(render: RenderFlightDetail) {
  const layer = useDetailLayer(render);
  const open: OpenFlightDetail = (flight: FaresPageFlight, kind, focus) => {
    const seg = flight.seg;
    layer.open({
      offerId: flight.offerId,
      ...(focus ? { focus } : {}),
      summary: {
        ...(seg.carrier ? { carrier: seg.carrier } : {}),
        ...(seg.carrierName ? { carrierName: seg.carrierName } : {}),
        ...(seg.flightNumber ? { flightNumber: seg.flightNumber } : {}),
        from: seg.from,
        to: seg.to,
        dir: kind,
      },
    });
  };
  return { open, node: layer.node };
}

/**
 * A tela "Escolha as tarifas" escrita a partir do PEDIDO: o cabeçalho com rota,
 * forma e cabine, o desvio de aeroporto da volta e o drawer de detalhe na camada
 * da tela. É a tela que o explorer e o card empilhado montam.
 */
export function FlightRequestFaresPage({
  request,
  text,
  airportSwitchOf,
  renderDetail,
  ...props
}: Readonly<FlightRequestFaresPageProps>) {
  const detail = useFaresDetail(renderDetail);
  const { outbound, inbound } = props;
  const found = inbound
    ? airportSwitchOf({
        outboundArrival: outbound.seg.to,
        returnDeparture: inbound.seg.from,
        requested: request.legs[0]?.destination,
      })
    : null;
  return (
    <>
      <FlightFaresPage
        {...props}
        subtitle={subtitleOf(request, text)}
        cabin={request.cabin ? text.request.cabin[request.cabin] : undefined}
        airportSwitch={found}
        onOpenDetail={detail.open}
        text={text}
      />
      {detail.node}
    </>
  );
}
